import type { ResultOutput } from './types.js';

/**
 * 集計（仕様書 §4.5）。
 *
 * MVP では対局を全件取得してオンザフライで集計する（§7.3 スケーリング方針）。
 * フロントのランキング表示と、将来のサーバー側統計エンドポイントの両方から
 * 同じ関数を使えるよう core に置いている。
 */

/** 集計の入力。1対局分の結果 */
export interface MatchForStats {
  playedAt: string;
  results: ResultOutput[];
}

export interface MemberStats {
  memberId: string;
  nameSnapshot: string;
  /** 累計ポイント。精算ポイントの総和 */
  totalPoint: number;
  /** 参加した対局数 */
  matchCount: number;
  /** 平均順位 */
  averageRank: number;
  /** 順位ごとの回数。添字0が1位 */
  rankCounts: number[];
  topRate: number;
  /** 連対率。(1位 + 2位) ÷ 対局数 */
  quinellaRate: number;
  /** ラス率。最下位の回数 ÷ 対局数 */
  lastRate: number;
  averageRawScore: number;
  bestPoint: number;
  worstPoint: number;
  /** 飛び賞を支払った回数 */
  bankruptCount: number;
  /** bustedBy に自分が指定された回数 */
  bustCount: number;
}

/**
 * メンバー別の成績を集計し、累計ポイントの降順で返す。
 *
 * 参加者数は対局ごとに異なりうる（4人打ちと3人打ちが混在するグループ）ため、
 * ラス率はその対局の人数を最下位として数える。
 */
export function aggregateStats(matches: MatchForStats[]): MemberStats[] {
  interface Acc {
    memberId: string;
    nameSnapshot: string;
    totalPoint: number;
    matchCount: number;
    rankSum: number;
    rankCounts: number[];
    lastCount: number;
    rawScoreSum: number;
    bestPoint: number;
    worstPoint: number;
    bankruptCount: number;
    bustCount: number;
  }

  const acc = new Map<string, Acc>();

  const ensure = (memberId: string, nameSnapshot: string): Acc => {
    let a = acc.get(memberId);
    if (!a) {
      a = {
        memberId,
        nameSnapshot,
        totalPoint: 0,
        matchCount: 0,
        rankSum: 0,
        rankCounts: [],
        lastCount: 0,
        rawScoreSum: 0,
        bestPoint: Number.NEGATIVE_INFINITY,
        worstPoint: Number.POSITIVE_INFINITY,
        bankruptCount: 0,
        bustCount: 0,
      };
      acc.set(memberId, a);
    } else if (nameSnapshot) {
      // 改名後も最新の表示名で並ぶよう、後の対局の名前で上書きする
      a.nameSnapshot = nameSnapshot;
    }
    return a;
  };

  for (const match of matches) {
    const playerCount = match.results.length;

    for (const r of match.results) {
      const a = ensure(r.memberId, r.nameSnapshot ?? '');
      a.totalPoint += r.totalPoint;
      a.matchCount += 1;
      a.rankSum += r.rank;
      a.rankCounts[r.rank - 1] = (a.rankCounts[r.rank - 1] ?? 0) + 1;
      if (r.rank === playerCount) a.lastCount += 1;
      a.rawScoreSum += r.rawScore;
      a.bestPoint = Math.max(a.bestPoint, r.totalPoint);
      a.worstPoint = Math.min(a.worstPoint, r.totalPoint);
      // 飛び賞を実際に支払ったときだけ数える。飛んでも fallbackPayTo=none で
      // 授受が発生しなければ支払いは無い
      if (r.isBankrupt && r.bankruptcyPoint < 0) a.bankruptCount += 1;

      if (r.bustedBy) {
        const busterName =
          match.results.find((x) => x.memberId === r.bustedBy)?.nameSnapshot ?? '';
        ensure(r.bustedBy, busterName).bustCount += 1;
      }
    }
  }

  return [...acc.values()]
    .map((a): MemberStats => {
      const n = a.matchCount;
      const filledRanks = Array.from(
        { length: a.rankCounts.length },
        (_, i) => a.rankCounts[i] ?? 0,
      );
      const top = filledRanks[0] ?? 0;
      const second = filledRanks[1] ?? 0;

      return {
        memberId: a.memberId,
        nameSnapshot: a.nameSnapshot,
        totalPoint: a.totalPoint,
        matchCount: n,
        averageRank: n > 0 ? a.rankSum / n : 0,
        rankCounts: filledRanks,
        topRate: n > 0 ? top / n : 0,
        quinellaRate: n > 0 ? (top + second) / n : 0,
        lastRate: n > 0 ? a.lastCount / n : 0,
        averageRawScore: n > 0 ? a.rawScoreSum / n : 0,
        bestPoint: n > 0 ? a.bestPoint : 0,
        worstPoint: n > 0 ? a.worstPoint : 0,
        bankruptCount: a.bankruptCount,
        bustCount: a.bustCount,
      };
    })
    .sort((x, y) => y.totalPoint - x.totalPoint);
}

/** 累計ポイントの推移（§4.5 推移グラフ）。古い対局から順に累積する */
export function cumulativePoints(
  matches: MatchForStats[],
  memberId: string,
): { playedAt: string; cumulative: number }[] {
  const ordered = [...matches].sort((a, b) => (a.playedAt < b.playedAt ? -1 : 1));
  const points: { playedAt: string; cumulative: number }[] = [];
  let sum = 0;

  for (const m of ordered) {
    const r = m.results.find((x) => x.memberId === memberId);
    if (!r) continue;
    sum += r.totalPoint;
    points.push({ playedAt: m.playedAt, cumulative: sum });
  }
  return points;
}
